// components/VendorTabs/SimpleBookingConfig.js - Complete Component
import React, { useState, useEffect } from 'react';

const SimpleBookingConfig = () => {
  const [simpleConfig, setSimpleConfig] = useState({
    mode: 'simple', // simple, capacity, services
    businessHours: {
      weekdayOpen: '09:00',
      weekdayClose: '17:00',
      saturdayOpen: '09:00',
      saturdayClose: '15:00',
      sundayOpen: false
    },
    capacity: {
      maxConcurrent: 3
    },
    // NEW — booking page settings (stored in widget_settings)
    bookingMode: 'availability', // 'availability' | 'open' | 'form_only' | 'simple'
    theme: 'branded',            // 'branded' (card) | 'plain' (transparent)
    // NEW — notifications & messages (stored in widget_settings)
    requireApproval: true,       // off => bookings auto-confirm, vendor email has no approve button
    sendCustomerEmail: true,     // off => no customer confirmation email
    successMessage: '',          // custom thank-you shown after submit (blank = default)
    customerEmailSubject: '',    // custom customer email subject (blank = default)
    customerEmailMessage: ''     // custom customer email body (blank = default)
  });

  const [saving, setSaving] = useState(false);

  // Load existing booking-page settings so the toggles reflect what's saved.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/booking-v2/vendor/config', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        if (res.ok) {
          const data = await res.json();
          const ws = data.template?.widget_settings || {};
          setSimpleConfig(prev => ({
            ...prev,
            bookingMode: ws.booking_mode || 'availability',
            theme: ws.theme || 'branded',
            requireApproval: ws.require_approval !== false,
            sendCustomerEmail: ws.send_customer_email !== false,
            successMessage: ws.success_message || '',
            customerEmailSubject: ws.customer_email_subject || '',
            customerEmailMessage: ws.customer_email_message || ''
          }));
        }
      } catch (err) {
        // Non-fatal — fall back to defaults.
        console.error('Could not load booking page settings:', err);
      }
    })();
  }, []);

  const saveSimpleConfig = async () => {
    try {
      setSaving(true);

      // Save business hours
      const businessHoursData = [
        // Monday-Friday
        ...Array.from({length: 5}, (_, i) => ({
          day_of_week: i + 1,
          is_open: true,
          open_time: simpleConfig.businessHours.weekdayOpen,
          close_time: simpleConfig.businessHours.weekdayClose
        })),
        // Saturday
        {
          day_of_week: 6,
          is_open: true,
          open_time: simpleConfig.businessHours.saturdayOpen,
          close_time: simpleConfig.businessHours.saturdayClose
        },
        // Sunday
        {
          day_of_week: 0,
          is_open: simpleConfig.businessHours.sundayOpen,
          open_time: simpleConfig.businessHours.sundayOpen ? '09:00' : simpleConfig.businessHours.weekdayOpen,
          close_time: simpleConfig.businessHours.sundayOpen ? '17:00' : simpleConfig.businessHours.weekdayClose
        }
      ];

      const hoursResponse = await fetch('/api/booking-v2/vendor/business-hours', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ businessHours: businessHoursData })
      });

      // Save capacity settings using V2 vendor profile route
      const capacityValue = simpleConfig.mode === 'simple' ? 999 : simpleConfig.capacity.maxConcurrent;

      const capacityResponse = await fetch('/api/booking-v2/vendor/profile', { // Changed to V2 route
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          default_concurrent_capacity: capacityValue,
          booking_v2_enabled: true // Also ensure V2 is enabled
        })
      });

      // Save booking-page settings (booking_mode + theme) into widget_settings.
      // Read the current template first so we preserve the vendor's form fields.
      let widgetSaveOk = true;
      const cfgRes = await fetch('/api/booking-v2/vendor/config', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (cfgRes.ok) {
        const cfg = await cfgRes.json();
        const tpl = cfg.template || {};
        const putRes = await fetch('/api/booking-v2/vendor/config', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            formFields: tpl.form_fields || [],
            widgetSettings: {
              ...(tpl.widget_settings || {}),
              booking_mode: simpleConfig.bookingMode,
              theme: simpleConfig.theme,
              require_approval: simpleConfig.requireApproval,
              send_customer_email: simpleConfig.sendCustomerEmail,
              success_message: simpleConfig.successMessage,
              customer_email_subject: simpleConfig.customerEmailSubject,
              customer_email_message: simpleConfig.customerEmailMessage
            },
            templateName: tpl.template_name || 'Default Form'
          })
        });
        widgetSaveOk = putRes.ok;
      } else {
        widgetSaveOk = false;
      }

      if (hoursResponse.ok && capacityResponse.ok && widgetSaveOk) {
        alert('Configuration saved successfully! Your V2 booking system is ready to use.');
      } else {
        throw new Error('Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="simple-config">
      <div className="bv2-section-header">
        <h2>Quick Setup Wizard</h2>
        <p>Get your booking system running in 3 simple steps</p>
      </div>

      {/* Step 1: Choose Mode */}
      <div className="config-step">
        <h3>Step 1: Choose Your Booking Style</h3>
        <div className="mode-options">
          <label className={`mode-card ${simpleConfig.mode === 'simple' ? 'active' : ''}`}>
            <input
              type="radio"
              value="simple"
              checked={simpleConfig.mode === 'simple'}
              onChange={(e) => setSimpleConfig(prev => ({...prev, mode: e.target.value}))}
            />
            <div className="mode-content">
              <h4>🟢 Simple & Easy</h4>
              <p>Unlimited bookings per time slot</p>
              <small>Perfect for group classes, events, or flexible scheduling</small>
            </div>
          </label>

          <label className={`mode-card ${simpleConfig.mode === 'capacity' ? 'active' : ''}`}>
            <input
              type="radio"
              value="capacity"
              checked={simpleConfig.mode === 'capacity'}
              onChange={(e) => setSimpleConfig(prev => ({...prev, mode: e.target.value}))}
            />
            <div className="mode-content">
              <h4>👥 Capacity Control</h4>
              <p>Limit bookings per time slot</p>
              <small>For multiple therapists, rooms, or limited capacity</small>
            </div>
          </label>
        </div>
      </div>

      {/* Step 2: Business Hours */}
      <div className="config-step">
        <h3>Step 2: Set Your Business Hours</h3>
        <div className="hours-grid">
          <div className="hour-row">
            <label>Weekdays (Mon-Fri)</label>
            <div className="time-inputs">
              <input
                type="time"
                value={simpleConfig.businessHours.weekdayOpen}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  businessHours: {...prev.businessHours, weekdayOpen: e.target.value}
                }))}
              />
              <span>to</span>
              <input
                type="time"
                value={simpleConfig.businessHours.weekdayClose}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  businessHours: {...prev.businessHours, weekdayClose: e.target.value}
                }))}
              />
            </div>
          </div>

          <div className="hour-row">
            <label>Saturday</label>
            <div className="time-inputs">
              <input
                type="time"
                value={simpleConfig.businessHours.saturdayOpen}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  businessHours: {...prev.businessHours, saturdayOpen: e.target.value}
                }))}
              />
              <span>to</span>
              <input
                type="time"
                value={simpleConfig.businessHours.saturdayClose}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  businessHours: {...prev.businessHours, saturdayClose: e.target.value}
                }))}
              />
            </div>
          </div>

          <div className="hour-row">
            <label>
              <input
                type="checkbox"
                checked={simpleConfig.businessHours.sundayOpen}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  businessHours: {...prev.businessHours, sundayOpen: e.target.checked}
                }))}
              />
              Open on Sundays
            </label>
          </div>
        </div>
      </div>

      {/* Step 3: Capacity (conditional) */}
      {simpleConfig.mode === 'capacity' && (
        <div className="config-step">
          <h3>Step 3: Set Your Capacity</h3>
          <div className="capacity-setting">
            <label>How many customers can book the same time slot?</label>
            <div className="capacity-input">
              <input
                type="number"
                min="1"
                max="20"
                value={simpleConfig.capacity.maxConcurrent}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  capacity: {...prev.capacity, maxConcurrent: parseInt(e.target.value)}
                }))}
              />
              <small>Number of therapists, rooms, or stations</small>
            </div>

            <div className="capacity-examples">
              <button
                type="button"
                onClick={() => setSimpleConfig(prev => ({
                  ...prev,
                  capacity: {...prev.capacity, maxConcurrent: 1}
                }))}
                className="example-btn"
              >
                1 Therapist
              </button>
              <button
                type="button"
                onClick={() => setSimpleConfig(prev => ({
                  ...prev,
                  capacity: {...prev.capacity, maxConcurrent: 3}
                }))}
                className="example-btn"
              >
                3 Stations
              </button>
              <button
                type="button"
                onClick={() => setSimpleConfig(prev => ({
                  ...prev,
                  capacity: {...prev.capacity, maxConcurrent: 5}
                }))}
                className="example-btn"
              >
                5 Staff Members
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 4: Booking Page (NEW) */}
      <div className="config-step">
        <h3>Step 4: Booking Page</h3>
        <div className="hours-grid">
          <div className="hour-row">
            <label>Booking flow</label>
            <select
              value={simpleConfig.bookingMode}
              onChange={(e) => setSimpleConfig(prev => ({ ...prev, bookingMode: e.target.value }))}
            >
              <option value="availability">Live availability — show open time slots</option>
              <option value="open">Open — any time within my hours</option>
              <option value="form_only">Form only — pick a date, no times</option>
              <option value="simple">Simple form — one screen, no steps</option>
            </select>
            <small>
              “Simple form” is a single-screen embed (image-1 style): name, date &amp; time pickers and your fields, no steps. “Form only” keeps the date step then details. Best paired with “Simple &amp; Easy” above so nothing is ever blocked.
            </small>
          </div>

          <div className="hour-row">
            <label>
              <input
                type="checkbox"
                checked={simpleConfig.theme === 'plain'}
                onChange={(e) => setSimpleConfig(prev => ({
                  ...prev,
                  theme: e.target.checked ? 'plain' : 'branded'
                }))}
              />
              Plain, transparent style (blends into my website)
            </label>
            <small>Removes the card border, background and shadow so the form inherits your site’s look.</small>
          </div>
        </div>
      </div>

      {/* Step 5: Notifications & Messages (NEW) */}
      <div className="config-step">
        <h3>Step 5: Notifications &amp; Messages</h3>
        <div className="hours-grid">
          <div className="hour-row">
            <label>
              <input
                type="checkbox"
                checked={simpleConfig.requireApproval}
                onChange={(e) => setSimpleConfig(prev => ({ ...prev, requireApproval: e.target.checked }))}
              />
              Require my approval before a booking is confirmed
            </label>
            <small>Turn off for this flow so bookings auto-confirm and your notification email won’t include an “Approve” button.</small>
          </div>

          <div className="hour-row">
            <label>
              <input
                type="checkbox"
                checked={simpleConfig.sendCustomerEmail}
                onChange={(e) => setSimpleConfig(prev => ({ ...prev, sendCustomerEmail: e.target.checked }))}
              />
              Send the customer a confirmation email
            </label>
          </div>

          <div className="hour-row">
            <label>Thank-you message (shown after submit)</label>
            <input
              type="text"
              placeholder="Thanks! We’ll be in touch to confirm."
              value={simpleConfig.successMessage}
              onChange={(e) => setSimpleConfig(prev => ({ ...prev, successMessage: e.target.value }))}
            />
          </div>

          {simpleConfig.sendCustomerEmail && (
            <>
              <div className="hour-row">
                <label>Customer email subject (optional)</label>
                <input
                  type="text"
                  placeholder="Your booking request"
                  value={simpleConfig.customerEmailSubject}
                  onChange={(e) => setSimpleConfig(prev => ({ ...prev, customerEmailSubject: e.target.value }))}
                />
              </div>
              <div className="hour-row">
                <label>Customer email message (optional)</label>
                <textarea
                  rows="3"
                  placeholder="We’ve received your request and will confirm shortly."
                  value={simpleConfig.customerEmailMessage}
                  onChange={(e) => setSimpleConfig(prev => ({ ...prev, customerEmailMessage: e.target.value }))}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Preview */}
      <div className="config-preview">
        <h3>Configuration Summary:</h3>
        <div className="preview-box">
          <p><strong>Booking Mode:</strong> {simpleConfig.mode === 'simple' ? 'Simple & Easy (Unlimited)' : 'Capacity Control'}</p>
          <p><strong>Hours:</strong> {simpleConfig.businessHours.weekdayOpen} - {simpleConfig.businessHours.weekdayClose} weekdays</p>
          {simpleConfig.mode === 'capacity' && (
            <p><strong>Capacity:</strong> {simpleConfig.capacity.maxConcurrent} bookings per time slot</p>
          )}
          <p><strong>Sunday:</strong> {simpleConfig.businessHours.sundayOpen ? 'Open' : 'Closed'}</p>
          <p><strong>Booking flow:</strong> {
            simpleConfig.bookingMode === 'simple' ? 'Simple form (one screen)' :
            simpleConfig.bookingMode === 'form_only' ? 'Form only (date + details)' :
            simpleConfig.bookingMode === 'open' ? 'Open (any time within hours)' :
            'Live availability'
          }</p>
          <p><strong>Widget style:</strong> {simpleConfig.theme === 'plain' ? 'Plain / transparent' : 'Branded card'}</p>
        </div>
      </div>

      {/* Save Button */}
      <div className="config-actions">
        <button
          onClick={saveSimpleConfig}
          disabled={saving}
          className="bv2-button bv2-button-primary bv2-button-large"
        >
          {saving ? 'Saving...' : 'Save Configuration & Enable Booking'}
        </button>
        <p className="save-note">
          Your booking system will be ready to use immediately after saving. You can always adjust these settings later.
        </p>
      </div>
    </div>
  );
};

export default SimpleBookingConfig;